import { useMemo } from 'react';
import type { Item } from '@/common/api/types/items';
import { useItemsBulkFetchQuery } from '@/common/queries/useGetItemsBulkFetchQuery';
import { commonQueryKeys } from '@/common/queryKeys/common';
import type { PublishedContextValue } from '@/features/views/components/published/PublishedContext';
import { usePublishedContext } from '@/features/views/components/published/PublishedContext';
import { usePublishedSnapshotQuery } from '@/features/views/components/published/usePublishedSnapshotQuery';

type ItemSummary = Pick<Item, 'id' | 'name' | 'itemTypeId'>;

function resolvePublishedItems(ctx: PublishedContextValue, itemIds: number[]): ItemSummary[] {
  const panelItemId = ctx.panelItemId;
  const panelData = panelItemId !== undefined ? ctx.getItemPanelData(panelItemId) : undefined;
  const resolvedItems = panelData?.resolvedItems ?? {};
  return itemIds
    .map((id): ItemSummary | undefined => {
      const resolved = resolvedItems[String(id)];
      if (resolved) return resolved;
      const details = ctx.getItemDetails(id);
      if (!details) return undefined;
      return { id: details.id, name: details.name, itemTypeId: details.itemTypeId };
    })
    .filter((item): item is ItemSummary => item !== undefined);
}

export const useItemsBulkFetchQueryWithPublished = ({ itemIds }: { itemIds: number[] }) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemsQuery = useItemsBulkFetchQuery({ itemIds }, undefined, !isPublished);

  const publishedQuery = usePublishedSnapshotQuery({
    baseKey: commonQueryKeys.itemsBulkFetch.byParams({ ids: itemIds }),
    getSnapshot: () =>
      publishedContext ? resolvePublishedItems(publishedContext, itemIds) : undefined,
  });

  const itemsSummary = useMemo<ItemSummary[] | undefined>(
    () => itemsQuery.data?.map((i) => ({ id: i.id, name: i.name, itemTypeId: i.itemTypeId })),
    [itemsQuery.data],
  );

  if (isPublished) return publishedQuery;
  return { ...itemsQuery, data: itemsSummary };
};
